import { Paper, Grid } from '@mui/material';
import Typography from '@mui/material/Typography';
import { styled } from '@mui/material/styles';
import Footer from '../components/Footer';
import React, { useState, useEffect } from 'react';
import { resolveSubscription } from '../modules/subscriptionManagement/resolveSubscription';
import { activateSubscription } from '../modules/subscriptionManagement/activateSubscription';
import LinearProgress from '@mui/material/LinearProgress';
import Alert from '@mui/material/Alert';
import { useMsal } from "@azure/msal-react";
import { sleep } from '../modules/sleep';

const Root = styled('div')(({ theme }) => ({
    padding: theme.spacing(2),
  }));
  
  const Container = styled(Paper)(({ theme }) => ({
    padding: theme.spacing(2),
    paddingBottom: theme.spacing(5),
  }));
  
  /*
  const LoginContainer = styled('div')(({ theme }) => ({
    alignItems: 'center',
    display: 'flex',
    flexGrow: 1,
    minHeight: '100%',
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(5),
  }));
  */
 
  const Title = styled(Typography)(({ theme }) => ({
    marginBottom: theme.spacing(3),
  }));

export default function AccountPage({setErrorMessage, loggedInAccount}) {
    const [purchasedSubscription, setPurchasedSubscription] = useState();
    const [isLoading, setIsLoading] = useState(false);
    const [isLoadingMessage, setIsLoadingMessage] = useState();
    const { instance } = useMsal();
    const { browserCredentials } = instance;
  
    
    useEffect(() => {
        const loadSubscription = async () => {
            const params = new URLSearchParams(window.location.search);
            if (loggedInAccount && params.has("token")) {
                //need to resolve the subscription
                setIsLoading(true);
                setIsLoadingMessage("Loading subscription details, please wait");
                try {
                    const token = loggedInAccount.accessToken;
                    const { data: subscription } = await resolveSubscription(params.get("token"), token);
                    window.appInsights.trackEvent({
                        name: "Subscription Resolved",
                        properties: subscription
                    });
                    window.dataLayer && window.dataLayer.push({
                        event: 'subscription_resolved',
                        auto_renew: subscription.autoRenew,
                        subscription_name: subscription.name,
                        subscriber: subscription.beneficiary?.emailId,
                        purchaser: subscription.purchaser?.emailId,
                        is_free_trial: subscription.isFreeTrial,
                    });
                    setPurchasedSubscription(subscription.subscription);
                    
                    // Automatically activate subscription if it needs activation
                    const needsActivation = subscription.subscription.saasSubscriptionStatus === "NotStarted" || 
                                           subscription.subscription.saasSubscriptionStatus === "PendingFulfillmentStart";
                    if (needsActivation) {
                        setIsLoadingMessage("Activating your subscription. This may take few minutes. Please don't close this window.");
                        try {
                            let activatedSubscription = await activateSubscription(subscription.subscription.id, browserCredentials);
                            window.appInsights.trackEvent({
                                name: "Subscription Activated",
                                properties: activatedSubscription
                            });
                            window.dataLayer && window.dataLayer.push({
                                event: 'subscription_activated',
                                auto_renew: activatedSubscription.autoRenew,
                                subscription_name: activatedSubscription.name,
                                subscriber: activatedSubscription.beneficiary?.emailId,
                                purchaser: activatedSubscription.purchaser?.emailId,
                                is_free_trial: activatedSubscription.isFreeTrial,
                            });
                            //keep loading subscription till we confirm its Subscribed as microsoft is doing an async process
                            if(activatedSubscription.saasSubscriptionStatus !== "Subscribed"){
                                do {
                                    await sleep(4000);
                                    const { data: reloadedSubscription } = await resolveSubscription(params.get("token"), loggedInAccount.accessToken);
                                    activatedSubscription = reloadedSubscription.subscription;
                                }  while (activatedSubscription.saasSubscriptionStatus !== "Subscribed");
                            }
                            setPurchasedSubscription(activatedSubscription);
                        } catch (activationError) {
                            window.appInsights.trackException({
                                exception: activationError,
                                properties: {
                                    process: "activating subscription"
                                }
                            });
                            setErrorMessage(`There was an error during activating subscription: ${activationError.message}.`);
                            setIsLoading(false);
                            setIsLoadingMessage("");
                            throw(activationError);
                        }
                    }
                    
                    setIsLoading(false);
                    setIsLoadingMessage("")
                } catch (error) {
                    window.appInsights.trackException({
                        exception: error,
                        properties: {
                            process: "resolving subscription"
                        }
                    });
                    setErrorMessage(`There was an error during resolving subscription: ${error.message}.`);
                    setIsLoading(false);
                    setIsLoadingMessage("");
                    throw (error);
                }
            }
        }
        loadSubscription();
    }, [loggedInAccount, setErrorMessage, browserCredentials ]);


    const getStatusActions = () => {
        switch (purchasedSubscription.saasSubscriptionStatus) {
            case "NotStarted":
            case "PendingFulfillmentStart":
                return <Alert severity="info">Subscription is being activated automatically. Please wait...</Alert>
            case "Subscribed":
                return <Alert severity="success">Your subscription is now active, to cancel or modify visit: <a href='https://portal.azure.com/#blade/HubsExtension/BrowseResourceBlade/resourceType/Microsoft.SaaS%2Fresources'>Azure Portal</a>. To use the app visit: <a href='/'>servicebus.cloudbricks.io</a></Alert>
            case "Suspended":
                return <Alert severity="warning">Your subscription is suspended, to resume subscription please fix billing in SaaS resource page in <a href='https://portal.azure.com/#blade/HubsExtension/BrowseResourceBlade/resourceType/Microsoft.SaaS%2Fresources'>Azure Portal</a></Alert>
            case "Unsubscribed":
                return <Alert severity="error">Selected subscription is not active, to resubscribe please create a new SaaS resource in <a href='https://portal.azure.com/#blade/HubsExtension/BrowseResourceBlade/resourceType/Microsoft.SaaS%2Fresources'>Azure Portal</a></Alert>
            default:
                return <Alert severity="error">{`Error: Unexpected subscription status: ${purchasedSubscription.saasSubscriptionStatus}`}</Alert>;
        }
    };

    return <><Root>
        {loggedInAccount ?
                <Grid container spacing={2} justifyContent="space-between">
                    <Grid size={{ xs: 12 }}>
                        <Container>
                            <Title  variant="h5" gutterBottom>
                                Subscription details
                            </Title>
                            <Grid container spacing={4} justifyContent="space-between">
                                {isLoading ? 
                                    <Grid size={{ xs: 12 }}>
                                        <Grid container spacing={1} justifyContent="space-between">
                                            <Grid size={{ xs: 12 }}>
                                                <LinearProgress />
                                            </Grid>
                                            <Grid size={{ xs: 12 }}>
                                                <Typography>{isLoadingMessage}</Typography>
                                            </Grid>
                                        </Grid>
                                    </Grid>
                                : <></>}
                                { purchasedSubscription ? 
                                <>
                                    <Grid size={{ xs: 1 }}>
                                        <Typography>Name</Typography>
                                    </Grid>
                                    <Grid size={{ xs: 11 }}>
                                        <Typography>{purchasedSubscription.name}</Typography>
                                    </Grid>
                                    <Grid size={{ xs: 1 }}>
                                        <Typography>Status</Typography>
                                    </Grid>
                                    <Grid size={{ xs: 11 }}>
                                        <Typography>{purchasedSubscription.saasSubscriptionStatus}</Typography>
                                    </Grid>
                                    <Grid size={{ xs: 1 }}>
                                        <Typography>Tenant Id</Typography>
                                    </Grid>
                                    <Grid size={{ xs: 11 }}>
                                        <Typography>{purchasedSubscription.beneficiary.tenantId}</Typography>
                                    </Grid>
                                    {purchasedSubscription.term.startDate ? <>
                                        <Grid size={{ xs: 1 }}>
                                            <Typography>Term</Typography>
                                        </Grid>
                                        <Grid size={{ xs: 11 }}>
                                            <Typography>{`(Monthly) Start: ${purchasedSubscription.term.startDate} - End: ${purchasedSubscription.term.endDate}`}</Typography>
                                        </Grid>
                                    </> : <></>}
                                    <Grid size={{ xs: 1 }}>
                                        <Typography>Auto Renew</Typography>
                                    </Grid>
                                    <Grid size={{ xs: 11 }}>
                                        <Typography>{purchasedSubscription.autoRenew ? "On": "Off"}</Typography>
                                    </Grid>
                                    <Grid size={{ xs: 12 }}>
                                        { getStatusActions() }
                                    </Grid>
                                </> 
                                : <></> 
                                }
                            </Grid>
                        </Container>
                    </Grid>
                </Grid>
            :
            <></>
        }
    </Root>
    <Footer />
    </>;
}